import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { CreateFunctionPayload } from '@polyai/stream/commands/handlers/functions/create-function/schema';
import type { DeleteFunctionPayload } from '@polyai/stream/commands/handlers/functions/delete-function/schema';
import type { UpdateFunctionPayload } from '@polyai/stream/commands/handlers/functions/update-function/schema';
import type { UpdateLatencyControlPayload } from '@polyai/stream/commands/handlers/functions/update-latency-control/schema';
import { updateVariable } from '@polyai/stream/store/reducers/variables';

import { assignMetadata } from '../../utils/assign-metadata';
import { getDefaultDate } from '../../utils/date';
import { replaceVariableNameInCode } from '../../utils/functions-helpers';
import {
  createFunction,
  deleteFunction,
  updateFunction,
  updateLatencyControl,
} from './actions';
import {
  delayResponsesAdapter,
  functionsAdapter,
  functionsReferencesAdapter,
  parametersAdapter,
} from './adapters';
import {
  delayResponsesSelectors,
  functionsSelectors,
  parametersSelectors,
} from './selectors';
import type {
  Function,
  FunctionDelayResponse,
  FunctionError,
  FunctionLatencyControl,
  FunctionParameter,
  FunctionReferences,
  ParameterType,
} from './types';
import {
  createFunctionWithEntityStates,
  getDefaultLatencyControl,
  registerFunctionReducersForFlowActions,
  registerFunctionReducersForTopicActions,
  registerMatchers,
  registerRulesOperations,
  registerStopkeywords,
} from './utils';

const SLICE_NAME = 'functions';

const slice = createSlice({
  name: SLICE_NAME,
  initialState: {
    functions: functionsAdapter.getInitialState(),
  },
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      createFunction,
      (state, action: PayloadAction<CreateFunctionPayload>) => {
        const newFunction = createFunctionWithEntityStates(
          action,
          action.payload,
        );

        functionsAdapter.addOne(state.functions, newFunction);
      },
    );

    builder.addCase(
      updateFunction,
      (state, action: PayloadAction<UpdateFunctionPayload>) => {
        const { id: functionId, parameters, ...update } = action.payload;
        const functionData = state.functions.entities[functionId];
        if (functionData) {
          if (parameters) {
            functionData.parameters = parametersAdapter.getInitialState(
              {},
              parameters.parameters.map(p => {
                const newParameter: FunctionParameter = {
                  ...p,
                  createdAt: '',
                  createdBy: '',
                  updatedAt: '',
                  updatedBy: '',
                };
                assignMetadata(action, newParameter);
                return newParameter;
              }),
            );
          }

          const newFunction: Function = {
            id: functionData.id,
            name: update.name ?? functionData.name,
            description: update.description ?? functionData.description,
            code: update.code ?? functionData.code,
            parameters: functionData.parameters,
            errors: update.errors?.errors ?? [],
            latencyControl: functionData.latencyControl,
            references: update.references ?? functionData.references,
            archived: !!update.archived,
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          };

          assignMetadata(action, newFunction);

          functionsAdapter.upsertOne(state.functions, newFunction);
        }
      },
    );

    builder.addCase(
      deleteFunction,
      (state, action: PayloadAction<DeleteFunctionPayload>) => {
        // Archive the function instead of removing it
        const existingFunction = state.functions.entities[action.payload.id];
        if (existingFunction) {
          existingFunction.archived = true;
          assignMetadata(action, existingFunction);
        }
      },
    );

    // Function Latency Control management
    builder.addCase(
      updateLatencyControl,
      (state, action: PayloadAction<UpdateLatencyControlPayload>) => {
        const { functionId, delayResponses, ...update } = action.payload;

        const existingFunction = state.functions.entities[functionId];
        const now = getDefaultDate();
        if (existingFunction) {
          if (delayResponses) {
            existingFunction.latencyControl.delayResponses =
              delayResponsesAdapter.getInitialState(
                {},
                delayResponses.delayResponses.map(d => {
                  const newDelayResponse: FunctionDelayResponse = {
                    ...d,
                    createdAt: '',
                    createdBy: '',
                    updatedAt: '',
                    updatedBy: '',
                  };
                  assignMetadata(action, newDelayResponse);
                  return newDelayResponse;
                }),
              );
          }

          const newLatencyControl: FunctionLatencyControl = {
            delayResponses: existingFunction.latencyControl.delayResponses,
            enabled: !!update.enabled,
            initialDelay: update.initialDelay ?? 0,
            interval: update.interval ?? 0,
            createdAt: now,
            updatedAt: now,
            createdBy: '',
            updatedBy: '',
          };

          assignMetadata(action, newLatencyControl);

          const newFunction: Function = {
            ...existingFunction,
            latencyControl: newLatencyControl,
            createdAt: now,
            updatedAt: now,
            createdBy: '',
            updatedBy: '',
          };

          assignMetadata(action, newFunction);

          functionsAdapter.upsertOne(state.functions, newFunction);
        }
      },
    );

    // When a variable is renamed, update code references
    builder.addCase(updateVariable, (state, action) => {
      const { id: variableId, name: newName, oldName } = action.payload;

      if (!newName || !oldName || newName === oldName) {
        return;
      }

      Object.values(state.functions.entities).forEach(func => {
        if (!func || !func.references?.variables?.[variableId]) {
          return;
        }
        functionsAdapter.updateOne(state.functions, {
          id: func.id,
          changes: {
            code: replaceVariableNameInCode(func.code, oldName, newName),
          },
        });
      });
    });

    // Flows & Steps references
    registerFunctionReducersForFlowActions(builder);

    // Topic references
    registerFunctionReducersForTopicActions(builder);

    // Behaviour references
    registerRulesOperations(builder);

    // Stop keywords references
    registerStopkeywords(builder);

    // registers matchers at the end
    // NOTE: matchers should be always defined below all extra reducers
    registerMatchers(builder);
  },
});

export type {
  Function,
  FunctionDelayResponse,
  FunctionError,
  FunctionLatencyControl,
  FunctionParameter,
  FunctionReferences,
  ParameterType,
};

export {
  createFunction,
  delayResponsesAdapter,
  delayResponsesSelectors,
  deleteFunction,
  functionsAdapter,
  functionsReferencesAdapter,
  functionsSelectors,
  getDefaultLatencyControl,
  parametersAdapter,
  parametersSelectors,
  SLICE_NAME,
  updateFunction,
  updateLatencyControl,
};

export default slice.reducer;
